import { createContext, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '@/lib/auth-context';
import { useIsDesktop } from '@/lib/native-context';

/**
 * Progress + completion status of the FIRST-LOGIN bulk download. Exposed
 * so a gate component (typically inside `ProtectedRoute`) can hold the
 * dashboard render until the local mirror is built, and so the
 * synchronisation screen can show "23 / 47 entités synchronisées".
 *
 * IMPORTANT: split into TWO contexts — one for status (changes on every
 * `step()` call) and one for actions (stable forever). Without the split,
 * `usePrewarmCache`'s `useEffect` would see the combined context object
 * change on every step and re-run, kicking off a NEW concurrent prewarm
 * pass on every increment. We had this exact bug — counter ballooned to
 * "3390 / 61" within seconds because dozens of overlapping loops were
 * all incrementing the same counter.
 *
 * Consumers:
 *   - `usePrewarmCache` reads ONLY actions (stable, no re-trigger).
 *   - `InitialSyncSplash` reads ONLY status (re-renders on each step).
 *   - `InitialSyncGate` reads status + auth (re-renders on each step too,
 *     but only when state flips between syncing/done).
 */
export interface InitialSyncStatus {
  /** 'idle' until the first task starts; 'syncing' while at least one
   *  task is in flight; 'done' once all tasks have settled (success or
   *  failure); 'failed' is reserved for a future fatal-abort path. */
  state: 'idle' | 'syncing' | 'done' | 'failed';
  total: number;
  completed: number;
  failed: number;
  /** Most recently completed task — used as a "Synchronisation de Articles…" hint. */
  currentLabel?: string;
}

export interface InitialSyncActions {
  /** Called by `usePrewarmCache` at start of a sync pass. Resets progress. */
  begin: (total: number) => void;
  /** Called after each task settles. */
  step: (ok: boolean, label?: string) => void;
  /** Called when the whole pass is done (success or with some failures). */
  finish: () => void;
  /** Mark this user as fully synced so future logins skip the gate. */
  markUserSynced: (userId: string) => void;
  /** Has this user already finished an initial sync? Read once on mount. */
  hasUserSynced: (userId: string) => boolean;
}

const INITIAL_STATUS: InitialSyncStatus = {
  state: 'idle',
  total: 0,
  completed: 0,
  failed: 0,
};

const InitialSyncStatusContext = createContext<InitialSyncStatus>(INITIAL_STATUS);
const InitialSyncActionsContext = createContext<InitialSyncActions | null>(null);

const SYNCED_USERS_LS_KEY = 'erp.initial-sync.users.v1';

function readSyncedUsers(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(SYNCED_USERS_LS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((v): v is string => typeof v === 'string'));
  } catch {
    return new Set();
  }
}

function writeSyncedUsers(users: Set<string>) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SYNCED_USERS_LS_KEY, JSON.stringify(Array.from(users)));
  } catch {
    /* quota / unavailable — silently ignore, gate will just block again next time */
  }
}

export function InitialSyncProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<InitialSyncStatus>(INITIAL_STATUS);
  const syncedUsersRef = useRef<Set<string>>(readSyncedUsers());

  // CRITICAL: empty deps — actions are stable forever. They reference
  // `setStatus` (stable by React contract) and `syncedUsersRef.current`
  // (mutable through the ref, never re-created). Adding `status` or any
  // other reactive value here would re-create actions on every change
  // and break the no-re-run invariant `usePrewarmCache` relies on.
  const actions = useMemo<InitialSyncActions>(
    () => ({
      begin: (total) => setStatus({ state: 'syncing', total, completed: 0, failed: 0 }),
      step: (ok, label) =>
        setStatus((s) => ({
          ...s,
          completed: s.completed + 1,
          failed: ok ? s.failed : s.failed + 1,
          currentLabel: label,
        })),
      finish: () => setStatus((s) => ({ ...s, state: 'done' })),
      markUserSynced: (uid) => {
        if (syncedUsersRef.current.has(uid)) return;
        syncedUsersRef.current.add(uid);
        writeSyncedUsers(syncedUsersRef.current);
      },
      hasUserSynced: (uid) => syncedUsersRef.current.has(uid),
    }),
    [],
  );

  return (
    <InitialSyncActionsContext.Provider value={actions}>
      <InitialSyncStatusContext.Provider value={status}>
        {children}
      </InitialSyncStatusContext.Provider>
    </InitialSyncActionsContext.Provider>
  );
}

export function useInitialSyncStatus(): InitialSyncStatus {
  return useContext(InitialSyncStatusContext);
}

export function useInitialSyncActions(): InitialSyncActions {
  const v = useContext(InitialSyncActionsContext);
  if (!v) throw new Error('useInitialSyncActions must be used within InitialSyncProvider');
  return v;
}

/**
 * Wraps content that should only render once the FIRST initial sync has
 * completed for the current user. Subsequent logins (the user has been
 * marked synced in localStorage) pass through immediately.
 *
 * Why the user-id check guards on top of `state`: there's a brief window
 * between the user becoming authenticated and the prewarm effect actually
 * calling `begin(...)`. Without the persisted-user check, the dashboard
 * would flash for a frame before the splash takes over. With it, the
 * gate engages from the moment we know the user has never synced.
 */
export function InitialSyncGate({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback: (status: InitialSyncStatus) => ReactNode;
}) {
  const isDesktop = useIsDesktop();
  const { user } = useAuth();
  const status = useInitialSyncStatus();
  const actions = useInitialSyncActions();

  // Web build has no offline mode — the prewarm runs to warm the React-
  // Query cache for perf, but there's no point blocking the dashboard
  // behind a "first sync" screen since the user has no expectation of
  // working offline in a browser tab. The splash is desktop-only.
  if (!isDesktop) return <>{children}</>;
  if (!user) return <>{children}</>;
  const alreadySynced = actions.hasUserSynced(user.id);
  if (alreadySynced || status.state === 'done') return <>{children}</>;
  return <>{fallback(status)}</>;
}
